import { Badge, Banner, Button, EmptyState, Spinner, TextField } from '@ui';
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { REPORT_BOARD } from '../../../shared/board';
import { BUG_STATUS_LABELS, bugApi, reportLabel, type BugReportDetail, type BugReportListItem, type BugStatus, type RepeatMode } from '../bugApi';
import { formatDateTime } from '../labels';
import { BOARD_OPEN_STATUSES, canDrop, columnCount, dropStatus, dropVerdict, layout, NO_FILTER, onBoard, resolutionLanes, type BoardFilter } from './layout';
import { RefreshIcon, SearchIcon } from '../icons';
import RevealOnOpen from './RevealOnOpen';
import { RepeatBadge, RepeatFilter } from './RepeatBlock';
import { applyDecision, onRepeatDecided } from './repeatBus';

type OnError = (err: unknown, fallback: string) => void;

const PAGES = 10;

async function loadReports(): Promise<BugReportListItem[]> {
  const open: BugReportListItem[] = [];
  let before: number | null = null;
  for (let page = 0; page < PAGES; page += 1) {
    const res = await bugApi.list({ status: BOARD_OPEN_STATUSES.join(','), limit: 100, before });
    open.push(...res.reports);
    before = res.nextBefore;
    if (!before) break;
  }
  const closed = await bugApi.list({ status: 'fixed,wont_fix,duplicate,closed', limit: 100 });
  return [...open, ...closed.reports.filter((r) => onBoard(r))];
}

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

export default function ReportBoard({
  onError,
  onMoved,
  renderDetail,
}: {
  onError: OnError;
  /** A status changed or a report went away: the tracker's counters follow. */
  onMoved?: (project: string, from: BugStatus, to: BugStatus | null) => void;
  renderDetail: (id: number, onChanged: (report: BugReportDetail) => void, onDeleted: () => void) => ReactNode;
}) {
  const [reports, setReports] = useState<BugReportListItem[] | null>(null);
  const [filter, setFilter] = useState<BoardFilter>(NO_FILTER);
  const [dragging, setDragging] = useState<BugReportListItem | null>(null);
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [problem, setProblem] = useState('');
  const [dupAsk, setDupAsk] = useState<{ card: BugReportListItem; original: string } | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setReports(await loadReports());
    } catch (err) {
      onError(err, 'Не удалось загрузить доску');
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  // Пов13: a decision on the open card changes the badge at once, the list is not reloaded.
  useEffect(() => onRepeatDecided((id, view) => setReports((prev) => (prev ? applyDecision(prev, id, view) : prev))), []);

  const dupFields = (r: Omit<BugReportListItem, 'attachments'>): Partial<BugReportListItem> => ({
    status: r.status,
    duplicateOf: r.duplicateOf,
    duplicateOfNumber: r.duplicateOfNumber,
    duplicateOfProject: r.duplicateOfProject,
    updatedAt: r.updatedAt,
  });
  const patch = (id: number, fields: Partial<BugReportListItem>) =>
    setReports((prev) => (prev ?? []).map((r) => (r.id === id ? { ...r, ...fields } : r)));

  /** Д5–Д6: the move carries the status seen; on any failure the card goes to the report's real column. */
  /** `duplicateNumber` — «дубль №» in the card's project (Т15). */
  const send = async (card: BugReportListItem, to: BugStatus, duplicateNumber?: number) => {
    const from = card.status;
    setProblem('');
    setPending((prev) => new Set(prev).add(card.id));
    patch(card.id, { status: to });
    try {
      const res = await bugApi.update(card.id, {
        status: to,
        expectedStatus: from,
        ...(duplicateNumber ? { duplicateNumber, duplicateProject: card.project } : {}),
      });
      patch(card.id, dupFields(res.report));
      // Кн4: a person is only warned by a gate: the card moved, the warning stays on the board.
      if (res.warnings?.length) setProblem(`${reportLabel(card)}: перенесено, но ${res.warnings[0]}`);
      if (res.report.status !== from) onMoved?.(card.project, from, res.report.status);
    } catch (err) {
      setProblem(`${reportLabel(card)}: ${errorText(err, 'не удалось перенести')}`);
      try {
        const fresh = await bugApi.get(card.id);
        patch(card.id, dupFields(fresh.report));
        if (fresh.report.status !== from) onMoved?.(card.project, from, fresh.report.status);
      } catch {
        patch(card.id, { status: from });
      }
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(card.id);
        return next;
      });
    }
  };

  /** Дк1: a drop is refused with the reason when the stages do not allow it; the card stays. */
  const drop = (to: BugStatus) => {
    const card = dragging;
    setDragging(null);
    if (!card || to === card.status) return;
    const verdict = dropVerdict(card.status, to);
    if (!verdict.ok) {
      setProblem(`${reportLabel(card)}: ${verdict.why}`);
      return;
    }
    // Д7: a duplicate needs the original's number first.
    if (to === 'duplicate') setDupAsk({ card, original: '' });
    else void send(card, to);
  };

  const confirmDup = (e: FormEvent) => {
    e.preventDefault();
    if (!dupAsk || !Number(dupAsk.original)) return;
    void send(dupAsk.card, 'duplicate', Number(dupAsk.original));
    setDupAsk(null);
  };

  const lane = useMemo(() => (reports ? layout(reports, filter) : null), [reports, filter]);

  if (!reports || !lane) return <Spinner label="Загрузка доски" />;

  return (
    <div className="board">
      <div className="board-toolbar">
        <TextField
          label="Поиск по доске"
          hideLabel
          size="sm"
          type="search"
          icon={<SearchIcon />}
          value={filter.text}
          onChange={(e) => setFilter({ ...filter, text: e.target.value })}
          onClear={filter.text ? () => setFilter({ ...filter, text: '' }) : undefined}
          placeholder="Номер, текст, автор"
          className="board-search"
        />
        <RepeatFilter
          value={filter.sign ? (filter.sign as RepeatMode) : filter.repeating ? 'repeat' : ''}
          onChange={(mode) => setFilter({ ...filter, repeating: mode === 'repeat', sign: mode === '' || mode === 'repeat' ? '' : mode })}
        />
        <Button size="sm" variant="quiet" icon={<RefreshIcon />} onClick={() => void load()}>
          Обновить
        </Button>
      </div>
      {problem && (
        <div role="alert" className="board-problem">
          <Banner tone="error" onClose={() => setProblem('')} closeLabel="Скрыть">
            {problem}
          </Banner>
        </div>
      )}
      {dupAsk && (
        <form className="board-dup" onSubmit={confirmDup}>
          <span>⧉ {reportLabel(dupAsk.card)} — дубль репорта {dupAsk.card.project} №</span>
          <TextField
            label="Номер оригинала"
            hideLabel
            size="sm"
            autoFocus
            inputMode="numeric"
            value={dupAsk.original}
            onChange={(e) => setDupAsk({ ...dupAsk, original: e.target.value.replace(/\D/g, '') })}
            className="board-dup-number"
          />
          <Button type="submit" size="sm" variant="primary" disabled={!Number(dupAsk.original) || Number(dupAsk.original) === dupAsk.card.id}>
            Перенести
          </Button>
          <Button type="button" size="sm" variant="quiet" onClick={() => setDupAsk(null)}>
            Отмена
          </Button>
        </form>
      )}
      {reports.length === 0 && <EmptyState compact>Репортов нет</EmptyState>}
      <div className="board-scroll">
        <div className="board-lane">
          <div className="board-columns" style={{ gridTemplateColumns: `repeat(${REPORT_BOARD.columns.length}, minmax(180px, 1fr))` }}>
            {lane.cells.map(({ column, cards }) => {
              const count = columnCount(reports, column);
              const target = dragging ? canDrop(column, dragging.status) : null;
              const card = (c: BugReportListItem) => (
                <button
                  key={c.id}
                  type="button"
                  className={`board-card${pending.has(c.id) ? ' is-pending' : ''}${openId === c.id ? ' is-open' : ''}`}
                  draggable={!pending.has(c.id)}
                  onDragStart={(e) => {
                    e.dataTransfer?.setData('text/plain', String(c.id));
                    setDragging(c);
                  }}
                  onDragEnd={() => setDragging(null)}
                  onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  aria-busy={pending.has(c.id)}
                >
                  <span className="board-card-top">
                    <span className="mono">{reportLabel(c)}</span>
                    {c.kind === 'ux' && <Badge tone="info">UX</Badge>}
                    {c.repeat && <RepeatBadge repeat={c.repeat} />}
                  </span>
                  <span className="board-card-text" data-pii="">
                    {c.description.split('\n')[0] || '(без описания)'}
                  </span>
                  <span className="board-card-meta">
                    <span data-pii="">
                      {c.reporter ?? '—'} · {formatDateTime(c.createdAt)}
                    </span>
                    {c.attachments.screenshots ? ' · снимок' : ''}
                  </span>
                </button>
              );
              return (
                <section
                  key={column.id}
                  className={`board-column${target === false ? ' is-blocked' : ''}${target ? ' is-target' : ''}`}
                  aria-label={column.title}
                  onDragOver={(e) => {
                    if (dragging) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(dropStatus(column, dragging?.status ?? 'backlog'));
                  }}
                >
                  <header className="board-column-head" data-bug-open="">
                    <span>{column.title}</span>
                    <span className="board-count">{count}</span>
                  </header>
                  {column.id === 'done'
                    ? resolutionLanes(column, cards).map((resolution) =>
                        resolution.cards.length > 0 || dragging ? (
                          <div
                            key={resolution.status}
                            className={`board-resolution${dragging && dropVerdict(dragging.status, resolution.status).ok ? ' is-target' : ''}`}
                            data-resolution={resolution.status}
                            onDragOver={(e) => {
                              if (dragging) e.preventDefault();
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              drop(resolution.status);
                            }}
                          >
                            <div className="board-resolution-head" data-bug-open="">
                              <span>{BUG_STATUS_LABELS[resolution.status]}</span>
                              <span className="board-count">{resolution.cards.length}</span>
                            </div>
                            {resolution.cards.map(card)}
                          </div>
                        ) : null,
                      )
                    : cards.map(card)}
                </section>
              );
            })}
          </div>
        </div>
      </div>
      {openId !== null && (
        <RevealOnOpen key={openId}>
        {renderDetail(
          openId,
          (report) => {
            const was = reports.find((r) => r.id === report.id);
            if (was && was.status !== report.status) onMoved?.(was.project, was.status, report.status);
            patch(report.id, dupFields(report));
          },
          () => {
            const gone = reports.find((r) => r.id === openId);
            if (gone) onMoved?.(gone.project, gone.status, null);
            setReports((prev) => (prev ?? []).filter((r) => r.id !== openId));
            setOpenId(null);
          },
        )}
        </RevealOnOpen>
      )}
    </div>
  );
}
